import { useAddProspectContactMutation } from "@apis/prospects/useProspects"
import {
  useGetContactTypesQuery,
  useGetContactDispositionsSerializedQuery,
} from "@apis/shared/useShared"
import { useAuthUserDetails } from "@apis/users/useUsers"
import type { IAddProspectContact } from "@apis/prospects/types"
import { AxiosError } from "axios"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@components/ui/form"
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@components/ui/select"
import { Button } from "@components/ui/button"
import { Textarea } from "@components/ui/textarea"
import { Loader2 } from "lucide-react"

type TAddContactFormProps = {
  prospect_guid: string
  onCancel: () => void
  onSubmitSuccess?: () => void
}

export const AddProspectContactForm = ({
  prospect_guid,
  onCancel,
  onSubmitSuccess,
}: TAddContactFormProps) => {
  const form = useForm<IAddProspectContact>({
    mode: "onTouched",
  })

  const { data: contactTypes = [] } = useGetContactTypesQuery()

  const { data: contactDispositions = [] } =
    useGetContactDispositionsSerializedQuery()

  const authUserDetails = useAuthUserDetails()
  const { mutate, isPending } = useAddProspectContactMutation()

  const handleAddContact = (formData: IAddProspectContact) => {
    mutate(
      {
        prospect_guid,
        comments: formData.comments.trim(),
        disposition_code: formData.disposition_code,
        type_name: formData.type_name,
        source_code: "INSPIRE",
        user_name: authUserDetails.data?.email || "",
      },
      {
        onSuccess: () => {
          toast.success("Contact successfully Added!")
          onSubmitSuccess?.()
        },
        onError: (error: string | AxiosError) =>
          toast.error(`Error adding contact! ${error}`),
      },
    )
  }

  return (
    <Form {...form}>
      <form onSubmit={(e) => e.preventDefault()} className="tw:space-y-6">
        <div className="tw:grid tw:grid-cols-1 tw:gap-4">
          <FormField
            control={form.control}
            name="type_name"
            rules={{ required: "Contact Type is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Contact Type</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl className="tw:w-full">
                    <SelectTrigger>
                      <SelectValue placeholder="Select Contact Type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {contactTypes.map(
                      (option: { value: string; label: string }) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="disposition_code"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Contact Disposition</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl className="tw:w-full">
                    <SelectTrigger>
                      <SelectValue placeholder="Select Contact Disposition" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {contactDispositions.map(
                      (option: { value: string; label: string }) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="comments"
            rules={{ required: "Comment is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Comments</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    placeholder="Add comments for this contact..."
                    rows={5}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="tw:flex tw:justify-end tw:gap-4">
          <Button
            type="submit"
            disabled={!form.formState.isValid || isPending}
            onClick={form.handleSubmit(handleAddContact)}
          >
            {isPending ? (
              <>
                <Loader2 className="tw:mr-2 tw:h-4 tw:w-4 tw:animate-spin" />
                Saving…
              </>
            ) : (
              "Save"
            )}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onCancel?.()}
          >
            Cancel
          </Button>
        </div>
      </form>
    </Form>
  )
}
